import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Storage } from '@ionic/storage-angular';
import { AngularFireAuth } from '@angular/fire/compat/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthErrorHandlerService {
  private isHandlingError = false;

  constructor(
    private router: Router,
    private storage: Storage,
    private afAuth: AngularFireAuth
  ) {}

  /**
   * Centralized handler for authentication errors (401, permission_denied, etc.)
   * This ensures we only handle the error once even if multiple services detect it
   */
  async handleAuthenticationError(source: string = 'unknown'): Promise<void> {
    // Prevent multiple simultaneous logout attempts
    if (this.isHandlingError) {
      console.log(`Authentication error already being handled, skipping duplicate from ${source}`);
      return;
    }

    this.isHandlingError = true;

    try {
      console.warn(`Authentication error detected from ${source} - clearing session and redirecting to login`);
      
      // Clear storage
      await this.storage.clear();
      
      // Sign out from Firebase
      await this.afAuth.signOut();
      
      // Redirect to login
      await this.router.navigate(['/login']);
    } catch (error) {
      console.error('Error handling authentication error:', error);
      // Force navigation even if there's an error
      try {
        await this.router.navigate(['/login']);
      } catch (navError) {
        console.error('Failed to navigate to login:', navError);
        // Last resort - reload the page
        window.location.href = '/login';
      }
    } finally {
      // Reset flag after a delay to allow the navigation to complete
      setTimeout(() => {
        this.isHandlingError = false;
      }, 1000);
    }
  }

  /**
   * Check if an error is an authentication/permission error
   */
  isAuthenticationError(error: any): boolean {
    if (!error) {
      return false;
    }

    // Check for HTTP 401
    if (error.status === 401) {
      return true;
    }

    // Check for Firebase permission denied errors
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorCode = error?.code?.toLowerCase() || '';
    
    return errorMessage.includes('permission_denied') || 
           errorMessage.includes('permission denied') ||
           errorCode === 'permission_denied';
  }
}

